import { AuthenticationService, NodeService } from 'app/data/services';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@app/data/common-imports';

@Component({
    selector: 'app-full-layout',
    templateUrl: './full.component.html',
    styleUrls: ['./full.component.scss']
})
export class FullComponent implements OnInit, OnDestroy {
    public version = environment.version;
    public isMaximized = false;
    public isFullScreen = false;
    public recorderState = { title: 'Starting recorder', subtitle: 'Preparing connection', visualState: 'booting' };
    private currentWindow: any = null;
    private recorderStateTimer: ReturnType<typeof setTimeout> | null = null;
    private readonly handleMaximize = () => this.isMaximized = true;
    private readonly handleUnmaximize = () => this.isMaximized = false;
    private readonly handleEnterFullScreen = () => this.isFullScreen = true;
    private readonly handleLeaveFullScreen = () => this.isFullScreen = false;
    private readonly handleRecorderState = (event: CustomEvent) => {
        if (!event.detail) return;

        // Dashboard can publish its initial recorder state while Angular is still
        // checking this parent layout. Apply it on the next turn so the title-bar
        // binding is stable for the complete change-detection pass.
        const nextState = event.detail;
        if (this.recorderStateTimer) clearTimeout(this.recorderStateTimer);
        this.recorderStateTimer = setTimeout(() => {
            this.recorderState = nextState;
            this.recorderStateTimer = null;
        }, 0);
    };

    constructor(private node: NodeService, public router: Router, private authenticationService: AuthenticationService) { }

    ngOnInit() {
        window.addEventListener('w3booster:recorder-state', this.handleRecorderState as EventListener);
        if (this.router.url === '/') {
            this.router.navigate(['/dashboard']);
        }
        if (this.node.isAvailable()) {
            this.currentWindow = this.node.remote.getCurrentWindow();
            this.isMaximized = this.currentWindow.isMaximized();
            this.isFullScreen = this.currentWindow.isFullScreen();
            this.currentWindow.on('maximize', this.handleMaximize);
            this.currentWindow.on('unmaximize', this.handleUnmaximize);
            this.currentWindow.on('enter-full-screen', this.handleEnterFullScreen);
            this.currentWindow.on('leave-full-screen', this.handleLeaveFullScreen);
        }
    }

    public ngOnDestroy(): void {
        window.removeEventListener('w3booster:recorder-state', this.handleRecorderState as EventListener);
        if (this.recorderStateTimer) clearTimeout(this.recorderStateTimer);
        if (this.currentWindow) {
            this.currentWindow.removeListener('maximize', this.handleMaximize);
            this.currentWindow.removeListener('unmaximize', this.handleUnmaximize);
            this.currentWindow.removeListener('enter-full-screen', this.handleEnterFullScreen);
            this.currentWindow.removeListener('leave-full-screen', this.handleLeaveFullScreen);
        }
    }

    public closeWindow() {
        if (this.node.isAvailable()) {
            this.node.remote.getCurrentWindow().close();
          }
    }

    public minimizeWindow() {
        if (this.node.isAvailable()) {
            this.node.remote.getCurrentWindow().minimize();
          }
    }

    public openRecorder(): void {
        window.dispatchEvent(new CustomEvent('w3booster:open-recorder'));
    }

    public toggleWindowMaximize(): void {
        if (!this.node.isAvailable()) return;
        const window = this.node.remote.getCurrentWindow();
        if (window.isFullScreen()) {
            window.setFullScreen(false);
            return;
        }
        window.isMaximized() ? window.unmaximize() : window.maximize();
    }

    public logout() {
        this.authenticationService.logout();
        this.router.navigate(['/login']);
    }
}
